import React from "react";

/**
 * Global Error Boundary
 * Catches JS errors anywhere in the React child tree and renders a graceful fallback
 * instead of a white screen of death.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console + (optionally) any monitoring system
    console.error("[Assagart ErrorBoundary]", error, errorInfo);
    if (window?.posthog?.captureException) {
      try {
        window.posthog.captureException(error, { extra: errorInfo });
      } catch (_) {}
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          background: "#0A0A0A",
          color: "#F5F5F5",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "'Manrope', system-ui, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.2rem, 4vw, 3rem)",
              color: "#D4AF37",
              marginBottom: "1rem",
              lineHeight: 1.1,
            }}
          >
            Something went wrong.
          </div>
          <p style={{ color: "#A3A3A3", marginBottom: "2rem", lineHeight: 1.6 }}>
            We hit an unexpected snag rendering this view. Your data is safe — please
            try reloading, or return to your dashboard.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              style={{
                background: "#D4AF37",
                color: "#0A0A0A",
                padding: "0.75rem 1.5rem",
                border: "none",
                borderRadius: "999px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Reload page
            </button>
            <button
              data-testid="error-boundary-home-btn"
              onClick={this.handleReset}
              style={{
                background: "transparent",
                color: "#F5F5F5",
                padding: "0.75rem 1.5rem",
                border: "1px solid #262626",
                borderRadius: "999px",
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Go home
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre
              style={{
                marginTop: "2rem",
                padding: "1rem",
                background: "#111",
                border: "1px solid #262626",
                borderRadius: 8,
                color: "#EF4444",
                textAlign: "left",
                fontSize: 12,
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
